<template>
  <RsModal
    :id="modalId"
    v-model="isVisible"
    :button-text="modalPrimaryBtnText"
    :has-main-button-right="hasMainButtonRight"
    :title="modalTitle"
    :size="modalSize"
    :disabled="disabled"
    :is-cancel="isCancel"
    @input="toggleModalVisibility"
    @submit="handleSubmit"
  >
    <slot name="header" />
    <div
      class="table-wrapper"
      :style="`height:${tableHeight}`"
    >
      <RsDataTableV2
        :id="tableId"
        :fields="tableFields"
        :data-list="tableDataList"
        :data-meta="tableListMeta"
        :is-loading="isLoadingTableList"
        :has-footer="hasTableFooter"
        :row-custom-styles="tableRowCustomColor as unknown as IRowCustomStyles"
        :has-no-border="hasNoTableBorder"
        :no-filter="hasNoTableFilter"
        :show-checkboxes="showCheckboxes"
        @on-filters-changed="getDataTableList"
        @on-checked-items-changed="$emit('on-checked-items-changed', $event)"
      >
        <template v-if="hasFilterLeft" #filterLeft>
          <span class="mb-3 filter-selected"> {{ hasFilterLeft }} </span>
        </template>
      </RsDataTableV2>
    </div>
  </RsModal>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { IDataTableEmitPayload, IDataTableFieldConfig, IMetaResponse } from '@/shared';
import { IRowCustomStyles } from '@/components/_global/RsDataTableV2.vue';

interface IRowCustomColor extends Record<string, any> { style?: Record<'color', string>, }

export default defineComponent({
  name: 'RsModalDataTable',
  props: {
    modalId: {
      type: String,
      default: '',
    },
    modalTitle: {
      type: String,
      default: '',
    },
    modalSize: {
      type: String,
      default: 'xl',
    },
    modalPrimaryBtnText: {
      type: String,
      default: 'Submit',
    },
    isModalVisible: {
      type: Boolean,
      default: false,
    },
    tableId: {
      type: String,
      default: 'rs-modal-table',
    },
    tableFields: {
      type: [] as PropType<IDataTableFieldConfig[]>,
      default() { return []; },
    },
    tableDataList: {
      type: Array as () => Record<string, any>[],
      default() {
        return [];
      },
    },
    tableListMeta: {
      type: Object as () => IMetaResponse,
      default() {
        return undefined;
      },
    },
    isLoadingTableList: {
      type: Boolean,
      default: false,
    },
    tableRowCustomColor: {
      type: Object as PropType<IRowCustomColor>,
      default: () => undefined,
    },
    tableHeight: {
      type: String,
      default: '250px',
    },
    hasTableFooter: {
      type: Boolean,
      default: false,
    },
    hasNoTableBorder: {
      type: Boolean,
      default: true,
    },
    hasNoTableFilter: {
      type: Boolean,
      default: true,
    },
    hasNoTablePadding: {
      type: Boolean,
      default: true,
    },
    showCheckboxes: {
      type: Boolean,
      default: false,
    },
    hasFilterLeft: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isCancel: {
      type: Boolean,
      default: true,
    },
    hasMainButtonRight: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['input', 'on-checked-items-changed', 'on-filters-changed', 'submit'],
  data() {
    return {
      isVisible: false as boolean,
    };
  },
  watch: {
    isModalVisible: {
      handler(newVal: boolean) {
        this.isVisible = newVal;
      },
    },
  },
  methods: {
    toggleModalVisibility(value: boolean): void {
      this.$emit('input', value);
    },
    handleSubmit() {
      this.$emit('submit');
    },
    getDataTableList(data: IDataTableEmitPayload) {
      this.$emit('on-filters-changed', data);
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.filter-selected {
  color: $secondary-color-dark;
}
</style>
